// Organization Map — v1.1 §3.7, build-instruction §3.5.
//
// The only surface in the migration with NO predecessor: nothing in the existing
// frontend consumes `/api/organization`, so this is purely additive and cannot
// regress anything. It is new *presentation* over an existing API, not a new
// capability (§0).
//
// WHAT IT WILL SHOW TONIGHT, AND WHY THAT IS CORRECT
// =================================================
// `/api/organization/*` is `require_founder` AND behind `CIPHER_ORGANIZATION_V1`,
// which is default-off and returns **404** when off — the same shape as the EA.
// So in every environment available to an unattended session this surface
// resolves to its `disabled` state. That is the surface working, not failing:
// the state is designed, it is labelled, and it tells the operator the truth.
//
// NO DEPARTMENT NAMES IN THIS FILE
// ===============================
// Every department label comes from an agent's `department` field via
// `deriveDepartments`. `/organization/summary` returns `departments` as a count,
// not a list, which is why the axis is derived from the roster. If the API's set
// ever differs from the eleven ratified in
// `docs/engineering/cipher-organizational-model-v1.md` §3, this renders what the
// API said — the difference is a finding, not something to correct in the client.
import React, { useMemo, useState } from "react";

import { useBrainResource } from "../../../components/brain/lib/hooks";
import EmptyState from "../../components/EmptyState";
import PageHead, { SectionTitle } from "../../components/PageHead";
import { useShell } from "../../ShellContext";
import AgentProfile from "./AgentProfile";
import OrgConstellation from "./OrgConstellation";
import { deriveDepartments, deriveRoster, resolveState } from "./orgModel";

/** The five non-ok states, each with copy that says what is actually true. */
const STATES = {
  loading: {
    title: "Loading the organization…",
    detail: "Reading departments and the agent roster.",
  },
  disabled: {
    title: "The organization surface is not enabled",
    detail:
      "CIPHER_ORGANIZATION_V1 is off, so /api/organization returns 404 — the surface does not exist yet rather than being empty. Enabling it is a Founder decision.",
  },
  denied: {
    title: "Founder access required",
    detail:
      "The server refused this request. Organization data is founder-only and the gate is server-side, so this is what a non-founder sees even by direct URL.",
  },
  empty: {
    title: "No agents materialized yet",
    detail:
      "The organization API responded, and the roster is empty. Departments and agents appear here once the organization has been materialized.",
  },
  error: {
    title: "Could not read the organization",
    detail: "The request failed. Nothing has been changed.",
  },
};

function LifecycleCounts({ counts }) {
  const entries = Object.entries(counts || {});
  if (entries.length === 0) return null;
  return (
    <div className="om-counts">
      {entries.map(([value, n]) => (
        <span key={value} className="chip om-chip">
          <span className="dot" data-lifecycle={value} />
          {value} <b className="num">{n}</b>
        </span>
      ))}
    </div>
  );
}

function AgentRow({ agent, onOpen }) {
  const label = agent.title || agent.agent_id;
  return (
    <li className="om-agent">
      <button
        type="button"
        className="om-agent-btn"
        onClick={() => onOpen(agent)}
        aria-label={`Open profile for ${label}`}
      >
        <span className="om-agent-name">{label}</span>
        <span className="om-agent-meta dim">
          {agent.layer ? <span className="om-layer">{agent.layer}</span> : null}
          {agent.lifecycle ? (
            <span className="om-life" data-lifecycle={agent.lifecycle}>
              {agent.lifecycle}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

export default function OrganizationMap() {
  const { overlays } = useShell();
  const [proposed, setProposed] = useState(null);

  // Reuses the existing wired data layer rather than adding a second one.
  // `useBrainResource` already distinguishes 404 from other failures, which is
  // exactly the distinction this surface needs.
  const agentsResource = useBrainResource("/organization/agents");

  const state = resolveState(agentsResource);
  const departments = useMemo(
    () => deriveDepartments(agentsResource.data),
    [agentsResource.data]
  );
  const roster = useMemo(
    () => deriveRoster(agentsResource.data),
    [agentsResource.data]
  );

  function openProfile(agent) {
    // The shell owns the overlay stack, so the drawer is requested, not rendered
    // here (§11.5). Falling back to inline rendering if overlays are absent
    // would put two components in charge of the top layer.
    if (overlays && typeof overlays.openDrawer === "function") {
      overlays.openDrawer(
        <AgentProfile agent={agent} onProposeChange={setProposed} />,
        { label: `Agent profile: ${agent.title || agent.agent_id}` }
      );
    }
  }

  return (
    <>
      <PageHead
        title="Organization Map"
        subtitle="Every agent, what it holds, and who it reports to."
      />

      {state !== "ok" ? (
        <EmptyState
          title={STATES[state].title}
          detail={STATES[state].detail}
        />
      ) : (
        <div className="om">
          {/* The constellation renders the same roster spatially: eleven
              department nodes ringing the Brain, each fanning out to its own
              agents. It reads `agentsResource.data` through `normalizeAgents`,
              the same helper the list below uses — one source, two readings.
              The list stays: it is what you scan and search; the map is what
              you look at. */}
          <OrgConstellation
            payload={agentsResource.data}
            masterSrc={`${process.env.PUBLIC_URL || ""}/brain/brain-master.png`}
          />

          <section className="om-roster" aria-label="Roster totals">
            <div className="om-total">
              <div className="label">Agents</div>
              <div className="om-total-n num">{roster.total}</div>
            </div>
            <div className="om-total">
              <div className="label">Departments</div>
              <div className="om-total-n num">{roster.departments}</div>
            </div>
            <LifecycleCounts counts={roster.byLifecycle} />
          </section>

          <SectionTitle>Departments</SectionTitle>
          <div className="om-depts">
            {departments.map((dept) => (
              <section
                key={dept.id || "unassigned"}
                className="card om-dept"
                aria-label={dept.label || "Unassigned"}
              >
                <header className="om-dept-head">
                  <h3 className="om-dept-name">
                    {dept.label || (
                      <span className="dim">Department not set</span>
                    )}
                  </h3>
                  <span className="om-dept-n num">{dept.total}</span>
                </header>
                <LifecycleCounts counts={dept.byLifecycle} />
                <ul className="om-agents">
                  {dept.agents.map((agent) => (
                    <AgentRow
                      key={agent.agent_id}
                      agent={agent}
                      onOpen={openProfile}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>

          {/* No utilization panel. Correction 5: the field does not exist on the
              agent record, so there is nothing to render and nothing to wait
              for. Stated on the surface so its absence reads as a decision
              rather than as an oversight. */}
          <p className="om-note dim">
            Agent utilization is not shown because the organization record does
            not carry it. Per-agent spend is derivable from a known objective's
            tasks, but nothing aggregates it and no route enumerates objectives.
          </p>
        </div>
      )}

      {proposed ? (
        <div className="om-proposed state" role="status">
          Proposing a change to the Brain for{" "}
          <b>{proposed.title || proposed.agent_id}</b> is not wired in this
          lane. The affordance is present because §3.7 makes it the only way the
          organization changes; the conversation it opens is a later step.
        </div>
      ) : null}
    </>
  );
}
